/** /academy: presentation, sign-up (or sign-in) and the 6-digit code received by e-mail. */

import {
  ApiError,
  type LanguageEnum,
  useAcademyRegisterCreate,
  useAcademySalesOfferRetrieve,
  useAuthOtpRequestCreate,
  useAuthOtpVerifyCreate,
} from "@agencyos/api-client";
import { asFx, detectTimeZone, formatXaf, guessCountry, indicativePrice, isExamKey, phoneFor } from "@agencyos/core";
import { Button, GlassCard, SelectField, TextField } from "@agencyos/ui-web";
import { CheckCircle2, Clock, Sparkles } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { session } from "../lib/session";
import { fieldErrors } from "../lib/settings";
import { ExamTracks } from "./ielts/ExamTracks";
import { IELTS, TCF, estimateKey, useActiveExam } from "./ielts/exam";
import { MotherTongueSelect } from "./ielts/MotherTongue";
import { readCampaign } from "./sales/campaign";
import { CountrySelect } from "./sales/CountrySelect";
import { SKILLS, type Skill } from "./types";
import { ActivityRings, SkillIcon } from "./ui";

type Step = "register" | "signin" | "code";

export function AcademyLanding() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // D-160: the exam of the sign-up: the « ?exam= » of the site's link, else the track picked below
  const { exam, setExam } = useActiveExam();
  const [params] = useSearchParams();
  const wanted = params.get("exam");
  useEffect(() => {
    if (isExamKey(wanted)) setExam(wanted);
  }, [wanted, setExam]);
  const [step, setStep] = useState<Step>("register");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [language, setLanguage] = useState<LanguageEnum>(i18n.resolvedLanguage === "en" ? "en" : "fr");
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [timeZone] = useState(detectTimeZone);
  const [country, setCountry] = useState(() => guessCountry(timeZone, navigator.languages ?? [navigator.language]));
  const campaign = readCampaign();
  const offer = useAcademySalesOfferRetrieve({ country: country || undefined }, { query: { retry: false } });
  const pass = offer.data?.status === 200 && offer.data.data.available ? offer.data.data : undefined;
  const indicative = pass
    ? indicativePrice(pass.price_xaf, country, asFx(pass.fx), i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR")
    : null;
  const [tongue, setTongue] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const register = useAcademyRegisterCreate();
  const requestCode = useAuthOtpRequestCreate();
  const verify = useAuthOtpVerifyCreate();
  const errors = fieldErrors(register.error);

  const explain = (failure: unknown): string => {
    if (failure instanceof ApiError && failure.status === 429) return t("auth.locked");
    if (failure instanceof ApiError && failure.detail) return failure.detail;
    if (!(failure instanceof ApiError) || failure.status >= 500) return t("auth.serverDown");
    return t("common.error");
  };

  const onRegister = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!consent) {
      setError(t("academy.landing.consentRequired"));
      return;
    }
    if (phone.trim() && country && !phoneFor(phone, country)) {
      setError(t("academySales.signup.phoneInvalid"));
      return;
    }
    register.mutate(
      {
        data: {
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
          language,
          consent,
          ...(tongue ? { mother_tongue: tongue } : {}),
          country,
          timezone: timeZone,
          marketing,
          exam,
          ...(Object.keys(campaign.utm).length ? { utm: campaign.utm } : {}),
          ...(campaign.referral ? { referral: campaign.referral } : {}),
          ...(pass ? { terms_version: pass.legal_version } : {}),
        },
      },
      {
        onSuccess: () => {
          void i18n.changeLanguage(language);
          setStep("code");
        },
        onError: (failure) => {
          if (!(failure instanceof ApiError && failure.status === 400)) setError(explain(failure));
        },
      },
    );
  };

  const onSignIn = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    requestCode.mutate(
      { data: { identifier: email.trim() } },
      { onSuccess: () => setStep("code"), onError: (failure) => setError(explain(failure)) },
    );
  };

  const onVerify = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    verify.mutate(
      { data: { identifier: email.trim(), code } },
      {
        onSuccess: (response) => {
          if (response.status !== 200) return;
          // A learner belongs to no agency team: no organization header is needed.
          session.signIn(response.data.access, null);
          navigate("/academy/home", { replace: true });
        },
        onError: (failure) =>
          setError(failure instanceof ApiError && failure.status === 400 ? t("auth.wrongCode") : explain(failure)),
      },
    );
  };

  // D-123: the hero follows the track picked below (it always spoke of the TCF Canada)
  const hero = (key: string) => t(exam === TCF ? `academy.landing.${key}` : [`academy.landing.byExam.${exam}.${key}`, `academy.landing.${key}`]);
  const skillName = (skill: Skill) => t(exam === IELTS ? `ielts.skill.${skill}` : `academy.skill.${skill}`);
  const features: { skill: Skill; text: string }[] = [
    { skill: "co", text: hero("featureExam") },
    { skill: "ee", text: hero("featureWriting") },
    { skill: "eo", text: hero("featureSpeaking") },
    { skill: "ce", text: hero("featurePlan") },
  ];

  return (
    <div className="ac-landing">
      <section className="ac-landing__hero">
        <p className="ac-landing__eyebrow">
          <Sparkles aria-hidden size={14} />
          {hero("eyebrow")}
        </p>
        <h1 className="ac-landing__title">{hero("title")}</h1>
        <p className="ac-landing__subtitle">{hero("subtitle")}</p>
        <div className="ac-landing__visual" aria-hidden="true">
          <ActivityRings
            rings={[
              { skill: "co", ratio: 0.92 },
              { skill: "ce", ratio: 0.74 },
              { skill: "ee", ratio: 0.58 },
              { skill: "eo", ratio: 0.81 },
            ]}
            size={176}
            stroke={15}
          />
          <div className="ac-landing__chips">
            {SKILLS.map((skill) => (
              <span key={skill} className={`ac-chip ac-skill--${skill}`}>
                {skillName(skill)}
              </span>
            ))}
          </div>
        </div>
        <ul className="ac-features">
          {features.map((feature) => (
            <li key={feature.text}>
              <SkillIcon skill={feature.skill} size={17} />
              <span>{feature.text}</span>
            </li>
          ))}
        </ul>
        <ExamTracks />
        {pass ? (
          <p className="as-landing-pass">
            <strong className="ao-num">{formatXaf(pass.price_xaf)}</strong>
            <span>{t("academySales.pass.perMonth")}</span>
            {indicative ? <span className="ac-secondary ao-num">{t("academySales.pass.indicative", { amount: indicative.text })}</span> : null}
            <span className="ac-footnote">{t("academySales.pass.title")} · {t("academySales.pass.noCommitment")}</span>
          </p>
        ) : null}
        <p className="ac-footnote ac-landing__note">
          <Clock aria-hidden size={13} /> {t(estimateKey(exam))}
        </p>
      </section>

      <GlassCard level="thick" className="ac-auth">
        {step === "register" && (
          <form className="ac-form" onSubmit={onRegister} noValidate>
            <div className="ac-auth__head">
              <h2 className="ac-title-2">{t("academy.landing.signupTitle")}</h2>
              <p className="ac-secondary">{t("academy.landing.signupHint")}</p>
            </div>
            <TextField
              label={t("academy.landing.email")}
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              required
            />
            <div className="ac-form-grid ac-form-grid--tight">
              <TextField
                label={t("academy.landing.firstName")}
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={errors.first_name}
                required
              />
              <TextField
                label={t("academy.landing.lastName")}
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={errors.last_name}
              />
            </div>
            <CountrySelect value={country} onChange={setCountry} error={errors.country} hint={t("academySales.signup.countryHint")} />
            <TextField
              label={t("academy.landing.phone")}
              hint={t("academySales.signup.phoneHint")}
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder={t("academy.landing.phonePlaceholder")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={errors.phone}
            />
            <SelectField
              label={t("academy.landing.language")}
              value={language}
              onChange={(e) => setLanguage(e.target.value === "en" ? "en" : "fr")}
              options={[
                { value: "fr", label: t("academy.landing.languageFr") },
                { value: "en", label: t("academy.landing.languageEn") },
              ]}
            />
            <MotherTongueSelect value={tongue} onChange={setTongue} />
            <label className="ac-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>
                {t("academy.landing.consent", { agency: (offer.data?.status === 200 && offer.data.data.agency) || t("academy.agencyFallback") })}{" "}
                <Trans
                  i18nKey="academySales.signup.terms"
                  components={{
                    terms: <Link className="ac-link" to="/academy/legal/terms" target="_blank" />,
                    privacy: <Link className="ac-link" to="/academy/legal/privacy" target="_blank" />,
                  }}
                />
              </span>
            </label>
            <label className="ac-consent">
              <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
              <span>{t("academySales.signup.marketing")}</span>
            </label>
            {campaign.referral ? <p className="ac-footnote">{t("academySales.signup.referral", { code: campaign.referral })}</p> : null}
            {timeZone ? <p className="ac-footnote">{t("academySales.signup.timezone", { zone: timeZone })}</p> : null}
            {errors.consent && <p className="ko">{errors.consent}</p>}
            {error && (
              <p className="ko" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" block disabled={register.isPending || !email || !firstName}>
              {register.isPending ? t("common.loading") : t("academy.landing.signup")}
            </Button>
            <Button variant="ghost" block onClick={() => (setStep("signin"), setError(null))}>
              {t("academy.landing.haveAccount")}
            </Button>
          </form>
        )}

        {step === "signin" && (
          <form className="ac-form" onSubmit={onSignIn} noValidate>
            <div className="ac-auth__head">
              <h2 className="ac-title-2">{t("academy.landing.signinTitle")}</h2>
            </div>
            <TextField
              label={t("academy.landing.email")}
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error && (
              <p className="ko" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" block disabled={requestCode.isPending || !email}>
              {requestCode.isPending ? t("common.loading") : t("auth.sendCode")}
            </Button>
            <Button variant="ghost" block onClick={() => (setStep("register"), setError(null))}>
              {t("academy.landing.noAccount")}
            </Button>
          </form>
        )}

        {step === "code" && (
          <form className="ac-form" onSubmit={onVerify}>
            <div className="ac-auth__head">
              <h2 className="ac-title-2">{t("academy.landing.codeTitle")}</h2>
            </div>
            <p role="status" className="ac-status ac-status--success">
              <CheckCircle2 aria-hidden size={18} />
              <span>{t("auth.codeSent", { identifier: email })}</span>
            </p>
            <TextField
              label={t("auth.codeLabel")}
              className="sign-code ac-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
            />
            {error && (
              <p className="ko" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" block disabled={verify.isPending || code.length < 6}>
              {verify.isPending ? t("common.loading") : t("auth.verify")}
            </Button>
            <div className="ac-auth__links">
              <Button variant="ghost" onClick={() => (setStep("signin"), setCode(""), setError(null))}>
                {t("auth.changeIdentifier")}
              </Button>
              <Button variant="ghost" onClick={() => requestCode.mutate({ data: { identifier: email.trim() } })}>
                {t("auth.resend")}
              </Button>
            </div>
          </form>
        )}
      </GlassCard>
    </div>
  );
}
